import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaChevronUp, FaChevronDown } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { playFreezeSound } from '../Utils/soundHandler';
import { useSettings, ARROW_SIZE } from '../contexts/SettingsContext';

interface Props {
  value: number;
  isFrozen: boolean;
  onToggleFreeze: () => void;
  onChange: (value: number) => void;
  soundEnabled: boolean; // Add soundEnabled as a prop
  isBot?: boolean;
  spinToValue?: number;
  spinning?: boolean;
}

const DiceContainer = styled(motion.div).withConfig({
  shouldForwardProp: (prop) => prop !== 'isFrozen',
})<{ isFrozen: boolean }>`
  width: 64px;
  height: 64px;
  background: #ffffff;
  color: #0d0d0d;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid rgba(0,0,0,0.2);
  border-radius: 10px;
  cursor: pointer;
  user-select: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background 0.2s ease, color 0.2s ease;
  box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.3);
  margin: 8px 0;

  ${({ isFrozen }) =>
    isFrozen &&
    `
    background: linear-gradient(180deg, rgba(120,160,220,1), rgba(70,120,210,1));
    color: #ffffff;
    border-color: rgba(0,100,220,0.9);
    box-shadow: 0 0 12px rgba(0, 90, 200, 0.7);
  `}
`;

const shakeAnimation = keyframes`
  0% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  50% { transform: translateX(5px); }
  75% { transform: translateX(-5px); }
  100% { transform: translateX(0); }
`;

const ShakingDiceContainer = styled(DiceContainer)`
  animation: ${shakeAnimation} 0.3s ease-in-out;
`;

const ArrowButton = styled.button`
  width: 64px;
  height: 26px;
  background: linear-gradient(180deg, rgb(0, 127, 255), rgb(0, 32, 64) 100%);
  border: none;
  border-radius: 5px;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 18px rgba(0, 127, 255, 0.35);
  }
`;

const DiceValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  font-family: 'Roboto Condensed', sans-serif;
`;

const DiceWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Dice: React.FC<Props> = ({
  value,
  isFrozen,
  onToggleFreeze,
  onChange,
  soundEnabled,
  isBot = false,
  spinToValue,
  spinning = false,
}) => {
  const [isShaking, setIsShaking] = useState(false);
  const [displayValue, setDisplayValue] = useState(value);

  // Player-adjustable arrow size + distance from the die (Settings page)
  const { settings } = useSettings();
  const arrow = ARROW_SIZE[settings.arrowSize];

  // Slot-machine spin animation for bot, controlled by 'spinning' prop
  React.useEffect(() => {
    let spinInterval: NodeJS.Timeout | undefined;
    if (isBot && spinning && typeof spinToValue === 'number') {
      let frame = 0;
      const totalFrames = 30 + Math.floor(Math.random() * 10); // Longer spin
      spinInterval = setInterval(() => {
        setDisplayValue((prev) => (prev % 6) + 1); // Cycle 1-6
        frame++;
        if (frame >= totalFrames) {
          clearInterval(spinInterval);
          setDisplayValue(spinToValue);
        }
      }, 40);
    } else if (!spinning && typeof spinToValue === 'number') {
      setDisplayValue(spinToValue);
    } else if (!isBot || typeof spinToValue !== 'number') {
      setDisplayValue(value);
    }
    return () => {
      if (spinInterval) clearInterval(spinInterval);
    };
  }, [isBot, spinToValue, value, spinning]);

  // Keep displayValue in sync with value for non-bot dice
  React.useEffect(() => {
    if (!isBot) setDisplayValue(value);
  }, [value, isBot]);

  const handleFreezeShake = () => {
    setIsShaking(true);
    setTimeout(() => setIsShaking(false), 300);
  };

  const increment = () => {
    if (isFrozen) {
      handleFreezeShake();
      return;
    }
    onChange((value + 1) % 7); // Loop back to 0 after reaching 6
  };

  const decrement = () => {
    if (isFrozen) {
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 300);
      return;
    }
    onChange(value === 0 ? 6 : value - 1); // Loop back to 6 from 0
  };

  const handleToggleFreeze = () => {
    // Disallow user-triggered freezing for bot dice
    if (isBot) {
      return;
    }
    if (!isFrozen) {
      onChange(6); // Automatically set to 6 when freezing
      if (soundEnabled) {
        playFreezeSound();
      }
    }
    onToggleFreeze();
  };

  // For human dice the configurable gap controls the space between arrow and die;
  // bot dice keep the default look. Gap is applied as the die's vertical margin.
  const dieMargin = isBot ? undefined : `${arrow.gap}px 0`;
  const arrowStyle = { height: arrow.height, fontSize: arrow.icon } as const;

  return (
    <DiceWrapper>
      {!isBot && (
        <ArrowButton onClick={increment} style={arrowStyle} aria-label="Increase dice value">
          <FaChevronUp />
        </ArrowButton>
      )}

      {isShaking ? (
        <ShakingDiceContainer isFrozen={isFrozen} onClick={!isBot ? handleToggleFreeze : undefined} style={{ cursor: isBot ? 'default' : undefined, margin: dieMargin }}>
          <DiceValue>{displayValue}</DiceValue>
        </ShakingDiceContainer>
      ) : (
        <DiceContainer isFrozen={isFrozen} onClick={!isBot ? handleToggleFreeze : undefined} style={{ cursor: isBot ? 'default' : undefined, margin: dieMargin }}>
          <DiceValue>{displayValue}</DiceValue>
        </DiceContainer>
      )}

      {!isBot && (
        <ArrowButton onClick={decrement} style={arrowStyle} aria-label="Decrease dice value">
          <FaChevronDown />
        </ArrowButton>
      )}
    </DiceWrapper>
  );
  
};

export default Dice;
